import { formFieldInstructions } from './helpers';
import { LiteElement, RestApiForm } from '../tasks/types';
import {
  assignmentField, combinedCreatedField,
  issueLinkField,
  locationField,
  repeatField,
  projectField,
} from '../editor/metadata-record-editor/metadata-fields';

export interface FieldChange {
  attributeName: string;
  value: any;
}

export interface Instruction {
  key: string;
  children?: Instruction[];
  changes?: FieldChange[];
}

const useFormFields = (form: RestApiForm, specialInstructions?: Instruction[]): {
  elements: Array<Element | LiteElement>, error: any
} => {
  let error: any;
  const elementsByKey: object = {};
  const instructions = formFieldInstructions(form);
  const orderedElements:Array<Element | LiteElement> = [];

  if (!form) {
    return {
      elements: [],
      error: '',
    };
  }

  const addKeyAndElement = (element: any) => {
    const { key } = element;
    elementsByKey[`${key}`] = element;
  };

  const organizeElementsByKey = (elements: Element[]) => {
    elements.forEach((element: any) => {
      addKeyAndElement(element);
      if (element.elements) {
        organizeElementsByKey(element.elements);
      }
    });
  };

  const copyElements = () => {
    form.elements.forEach((element: any) => {
      orderedElements.push(element);
    });
  };

  const buildMetadataFields = () => {
    if (form.assignment_enabled) {
      orderedElements.push(assignmentField);
    }

    if (form.geometry_types.length) {
      orderedElements.push(locationField);
    }

    if (form.projects_enabled) {
      orderedElements.push(projectField);
    }

    if (form.status_field.enabled) {
      orderedElements.push(form.status_field);
    }
  };

  const disableFieldError = (disabledKey: string) => {
    error = `The key ${disabledKey} is not enabled in the form.`;
  };

  const metadataElement = (instruction: Instruction) => {
    switch (instruction.key) {
      case '@assignment':
        if (form.assignment_enabled) {
          return assignmentField;
        }
        return disableFieldError('@assignment');
      case 'issue_link': {
        const issueLinkFieldWithFormId = {
          ...issueLinkField,
          form_id: form?.form_links?.from && form?.form_links?.from?.length > 0 ? form?.form_links?.from[0].form_id : '',
        };
        return issueLinkFieldWithFormId;
      }
      case '@location':
        if (form.geometry_types.length) {
          return locationField;
        }
        return disableFieldError('@location');
      case '@repeat':
        return repeatField;
      case '@project':
        if (form.projects_enabled) {
          return projectField;
        }
        return disableFieldError('@project');
      case '@status':
        if (form.status_field.enabled) {
          return form.status_field;
        }
        return disableFieldError('@status');
      case '@combined_created':
        return combinedCreatedField;
      default:
        return undefined;
    }
  };

  const changeAttributeValues = (
    instruction: Instruction,
    element: Element | LiteElement,
  ) => {
    instruction.changes?.forEach((change: FieldChange) => {
      // eslint-disable-next-line no-param-reassign
      element[change.attributeName] = change.value;
    });
  };

  const findSpecialInstructions = (key: string) => {
    if (!specialInstructions || specialInstructions.length === 0) {
      return [];
    }

    return specialInstructions.filter((special) => (special.key === key));
  };

  const processElements = (instruction: Instruction, elements: Array<Element | LiteElement>) => {
    const element = elementsByKey[instruction.key] || metadataElement(instruction);
    if (element) {
      const specials = findSpecialInstructions(instruction.key);
      if (instruction.changes) {
        changeAttributeValues(instruction, element);
      }
      specials.forEach((special) => {
        if (special.changes) {
          changeAttributeValues(special, element);
        }
      });

      if (element.elements) {
        const value = JSON.parse(JSON.stringify(element));
        value.elements = [];
        instruction.children?.forEach((child: Instruction) => {
          processElements(child, value.elements);
        });
        elements.push(value);
      } else {
        if (instruction.children) {
          error = `Element ${instruction.key} of type ${element.type} is not allowed to have children ${JSON.stringify(instruction.children)}`;
        }
        elements.push(element);
      }
    } else if (!error) {
      error = `Could not find element for key: ${instruction.key}`;
    }
  };

  try {
    if (!instructions) {
      buildMetadataFields();
      copyElements();
    } else {
      organizeElementsByKey(form.elements);
      instructions.forEach((i) => {
        processElements(i, orderedElements);
      });
    }
  } catch (e) {
    error = e;
  }

  if (error) {
    orderedElements.splice(0);
    console.error(error);
  }

  return { elements: orderedElements, error };
};

export default useFormFields;
